import { useState, useEffect } from "react";
import { Box, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, MenuItem, Select, InputLabel, FormControl, Typography, Card, CardContent, InputAdornment, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePostDataFetch } from "../../utils/useDataFetch";
import { useAuth } from "../../context/AuthContext";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const Login = () => {
    const theme = useTheme();
    const { data: loginData, error: loginError, isLoading: loginLoading, postData: postLogin } = usePostDataFetch();
    const { data: regData, error: regError, isLoading: regLoading, postData: postReg } = usePostDataFetch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnTo = searchParams.get("returnTo");

    const [openDialog, setOpenDialog] = useState(false);
    const [anmData, setAnmData] = useState({ email: "", password: "" });
    const [regFormData, setRegFormData] = useState({
        email: "", first_name: "", last_name: "", role: "", password: "",
        positions: []
    });

    const [openForgotDialog, setOpenForgotDialog] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const { data: forgotData, error: forgotError, isLoading: forgotLoading, postData: postForgot } = usePostDataFetch();

    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (forgotData?.success) {
            setOpenForgotDialog(false);
            setForgotEmail("");
            alert(forgotData.message || "Passwort-Reset E-Mail wurde gesendet.");
        }
    }, [forgotData]);

    const { login } = useAuth();

    useEffect(() => {
        if (loginData?.success) {
            if (loginData.token) {
                login(loginData.token, loginData.refreshToken, loginData.user)
                    .then(() => navigate(returnTo || "/"))
                    .catch((err) => console.error('Login fehlgeschlagen:', err));
            } else {
                alert(loginData.message || "Dein Account wird geprüft. Bitte warte auf Genehmigung.");
            }
        } else {
            console.log(loginData);
        }
    }, [loginData, navigate, returnTo, login]);

    useEffect(() => {
        if (regData?.success) {
            if (regData.token) {
                login(regData.token, regData.refreshToken, regData.user)
                    .then(() => {
                        setOpenDialog(false);
                        navigate(returnTo || "/");
                    })
                    .catch((err) => console.error('Registrierung/Login fehlgeschlagen:', err));
            } else {
                alert(regData.message || "Deine Registrierung wurde gesendet und wird geprüft.");
            }
        }
    }, [regData, navigate, returnTo, login]);

    const handleLogin = (e) => {
        e.preventDefault();
        postLogin({ url: "/auth/login", aBody: anmData, skipAuth: true });
    };

    const handleReg = (e) => {
        e.preventDefault();
        postReg({ url: "/auth/register", aBody: regFormData, skipAuth: true });
    };

    const handleForgotPassword = (e) => {
        e.preventDefault();
        postForgot({ url: "/auth/forgot-password", aBody: { email: forgotEmail }, skipAuth: true });
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 2 }}>
            <Card sx={{ maxWidth: 450, width: '100%', boxShadow: 6, borderRadius: "12px" }}>
                <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h3" sx={{ fontWeight: 900, mb: 1, textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
                            SGE <span style={{ color: theme.palette.primary.main }}>Manager</span>
                        </Typography>
                        <Typography variant="body1" color="text.secondary">Bitte melde dich an, um fortzufahren</Typography>
                    </Box>

                    <form onSubmit={handleLogin}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <TextField
                                label="E-Mail Adresse"
                                type="email"
                                required
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment>,
                                }}
                                onChange={(e) => setAnmData({ ...anmData, email: e.target.value })}
                            />
                            <TextField
                                label="Passwort"
                                type={showPassword ? 'text' : 'password'}
                                required
                                fullWidth
                                variant="outlined"

                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><LockIcon color="action" /></InputAdornment>,
                                    endAdornment:
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={
                                                    showPassword ? 'hide the password' : 'display the password'
                                                }
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                }}
                                onChange={(e) => setAnmData({ ...anmData, password: e.target.value })}
                            />

                            {loginError && (
                                <Typography color="error" variant="body2" align="center" sx={{ bgcolor: 'error.main', color: 'error.contrastText', p: 1, borderRadius: "12px" }}>
                                    {typeof loginError === 'object' ? loginError.message || "Anmeldung fehlgeschlagen." : loginError || "Anmeldung fehlgeschlagen."}
                                    <br />
                                    Bitte Zugangsdaten prüfen.
                                </Typography>
                            )}

                            <Button type="submit" variant="contained" color="primary" size="large" fullWidth disabled={loginLoading} sx={{ mt: 1, py: 1.8, fontSize: '1.1rem', fontWeight: 'bold', borderRadius: "12px" }}>
                                {loginLoading ? "Wird angemeldet..." : "Login"}
                            </Button>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Button variant="text" color="text.secondary" onClick={() => setOpenDialog(true)}>
                                    Neu registrieren
                                </Button>
                                <Button variant="text" color="text.secondary" onClick={() => setOpenForgotDialog(true)}>
                                    Passwort vergessen?
                                </Button>
                            </Box>
                        </Box>
                    </form>
                </CardContent>
            </Card>

            {/* Forgot Password Dialog */}
            <Dialog open={openForgotDialog} onClose={() => setOpenForgotDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">Passwort zurücksetzen</Typography>
                    <IconButton onClick={() => setOpenForgotDialog(false)} size="small" sx={{ bgcolor: 'action.hover' }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Gib deine E-Mail-Adresse ein, um einen Link zum Zurücksetzen deines Passworts zu erhalten.
                    </Typography>
                    <form id="forgot-form" onSubmit={handleForgotPassword}>
                        <TextField
                            label="E-Mail Adresse"
                            type="email"
                            required
                            fullWidth
                            variant="outlined"
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                        />
                        {forgotError && (
                            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                                {typeof forgotError === 'object' ? forgotError.message : forgotError}
                            </Typography>
                        )}
                        {forgotData?.success === false && !forgotError && (
                            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                                {forgotData.message}
                            </Typography>
                        )}
                    </form>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenForgotDialog(false)} color="inherit" variant="text">Abbrechen</Button>
                    <Button type="submit" form="forgot-form" variant="contained" disabled={forgotLoading}>
                        {forgotLoading ? "Wird gesendet..." : "Link anfordern"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Registrierungs-Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
                    <Typography variant="h5" fontWeight="bold">Neu registrieren</Typography>
                    <IconButton onClick={() => setOpenDialog(false)} size="small" sx={{ bgcolor: 'action.hover' }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <form id="reg-form" onSubmit={handleReg}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <TextField label="Vorname" type="text" required fullWidth onChange={(e) => setRegFormData({ ...regFormData, first_name: e.target.value })} />
                                <TextField label="Nachname" type="text" required fullWidth onChange={(e) => setRegFormData({ ...regFormData, last_name: e.target.value })} />
                            </Box>

                            <TextField label="E-Mail Adresse" type="email" required fullWidth onChange={(e) => setRegFormData({ ...regFormData, email: e.target.value })} />

                            <FormControl fullWidth required>
                                <InputLabel>Deine primäre Rolle</InputLabel>
                                <Select value={regFormData.role} label="Deine primäre Rolle" onChange={(e) => setRegFormData({ ...regFormData, role: e.target.value })}>
                                    <MenuItem value="trainer">Trainer</MenuItem>
                                    <MenuItem value="spieler">Spieler</MenuItem>
                                </Select>
                            </FormControl>

                            <Typography variant="body2" sx={{ mt: 1, mb: -1, fontWeight: 'bold' }}>Deine Positionen (min. 1)</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {['Torwart', 'Verteidigung', 'Mittelfeld', 'Sturm', 'Trainer'].map(pos => (
                                    <Button
                                        key={pos}
                                        size="small"
                                        variant={regFormData.positions.includes(pos) ? "contained" : "outlined"}
                                        onClick={() => {
                                            const current = regFormData.positions;
                                            if (current.includes(pos)) {
                                                setRegFormData({ ...regFormData, positions: current.filter(p => p !== pos) });
                                            } else {
                                                setRegFormData({ ...regFormData, positions: [...current, pos] });
                                            }
                                        }}
                                        sx={{ borderRadius: "12px" }}
                                    >
                                        {pos}
                                    </Button>
                                ))}
                            </Box>

                            <TextField label="Passwort" type="password" required fullWidth onChange={(e) => setRegFormData({ ...regFormData, password: e.target.value })} />

                            {regError && (
                                <Typography color="error" variant="body2" sx={{ bgcolor: 'error.main', color: 'error.contrastText', p: 1, borderRadius: "12px" }}>
                                    {typeof regError === 'object' ? regError.message || "Registrierung fehlgeschlagen" : regError || "Registrierung fehlgeschlagen"}
                                </Typography>
                            )}
                        </Box>
                    </form>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenDialog(false)} color="inherit" variant="text">Abbrechen</Button>
                    <Button type="submit" form="reg-form" variant="contained" disabled={regLoading} sx={{ px: 4, py: 1 }}>Profil erstellen</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Login;